import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiUser, FiMail, FiLock, FiAlertCircle } from 'react-icons/fi';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'buyer',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const { confirmPassword, ...registerData } = formData;
    const result = await register(registerData);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FFF9D4] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-display font-bold text-[#386646] mb-2">
            Create Account
          </h2>
          <p className="text-[rgba(34,34,34,0.7)]">
            Join RentHub today
          </p>
        </div>

        <div className="card p-8">
          {error && (
            <div className="mb-6 p-4 bg-[#FFF9D4] border-2 border-[#F27D16] rounded-[12px] flex items-start space-x-3">
              <FiAlertCircle className="text-[#F27D16] flex-shrink-0 mt-0.5 text-lg" />
              <p className="text-sm text-[#386646]">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-[#386646] mb-3">
                I want to
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'buyer' })}
                  className={`p-4 rounded-[12px] border-2 transition-all duration-200 ${
                    formData.role === 'buyer'
                      ? 'border-[#F27D16] bg-[#FFF9D4]'
                      : 'border-[rgba(34,34,34,0.2)] hover:border-[rgba(34,34,34,0.3)]'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">🛍️</div>
                    <div className="font-medium text-[#386646]">Rent Items</div>
                    <div className="text-xs text-[rgba(34,34,34,0.6)] mt-1">Buyer</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, role: 'seller' })}
                  className={`p-4 rounded-[12px] border-2 transition-all duration-200 ${
                    formData.role === 'seller'
                      ? 'border-[#F27D16] bg-[#FFF9D4]'
                      : 'border-[rgba(34,34,34,0.2)] hover:border-[rgba(34,34,34,0.3)]'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-2xl mb-2">💼</div>
                    <div className="font-medium text-[#386646]">List Items</div>
                    <div className="text-xs text-[rgba(34,34,34,0.6)] mt-1">Seller</div>
                  </div>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#386646] mb-2">
                Full Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field pl-10"
                  placeholder="John Doe"
                />
                <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(34,34,34,0.5)]" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#386646] mb-2">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-field pl-10"
                  placeholder="you@example.com"
                />
                <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(34,34,34,0.5)]" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#386646] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="input-field pl-10"
                  placeholder="••••••••"
                />
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(34,34,34,0.5)]" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#222222] mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="input-field pl-10"
                  placeholder="••••••••"
                />
                <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[rgba(34,34,34,0.5)]" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[rgba(34,34,34,0.7)]">
              Already have an account?{' '}
              <Link to="/login" className="text-[#F27D16] hover:text-[#D96D0E] font-medium">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
